import { useLayoutEffect, useState, useSyncExternalStore } from 'react';

import { clipBox, sameBox, toBox, type Box, type Clip, type Size } from '../engine/geometry';

const CLIPPING = /auto|scroll|hidden|clip/;

interface ClipAncestor {
  element: HTMLElement;
  x: boolean;
  y: boolean;
}

function clipAncestors(element: HTMLElement): ClipAncestor[] {
  const found: ClipAncestor[] = [];
  for (
    let parent = element.parentElement;
    parent && parent !== document.body && parent !== document.documentElement;
    parent = parent.parentElement
  ) {
    const style = getComputedStyle(parent);
    const x = CLIPPING.test(style.overflowX);
    const y = CLIPPING.test(style.overflowY);
    if (x || y) found.push({ element: parent, x, y });
  }
  return found;
}

function measure(element: HTMLElement, ancestors: readonly ClipAncestor[]): Box {
  const clips: Clip[] = ancestors.map(({ element: parent, x, y }) => {
    const rect = parent.getBoundingClientRect();
    return {
      box: {
        top: rect.top + parent.clientTop,
        left: rect.left + parent.clientLeft,
        width: parent.clientWidth,
        height: parent.clientHeight,
      },
      x,
      y,
    };
  });
  return clipBox(toBox(element.getBoundingClientRect()), clips);
}

/**
 * The visible part of the element in viewport coordinates, the one box the spotlight and the
 * card's anchor are both drawn from. Measured before paint, then once per frame: transforms,
 * slide-in animations and scrolling inside any container move an element without firing an
 * event the layer could listen to.
 */
export function useTrackedRect(element: HTMLElement | null): Box | null {
  const [tracked, setTracked] = useState<{ element: HTMLElement; box: Box } | null>(null);

  useLayoutEffect(() => {
    if (!element) return;
    const ancestors = clipAncestors(element);
    let frame = 0;
    const update = () => {
      if (element.isConnected) {
        const box = measure(element, ancestors);
        setTracked((prev) =>
          prev?.element === element && sameBox(prev.box, box) ? prev : { element, box },
        );
      }
      frame = requestAnimationFrame(update);
    };
    update();
    return () => cancelAnimationFrame(frame);
  }, [element]);

  return element && tracked?.element === element ? tracked.box : null;
}

function subscribeViewport(onChange: () => void) {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
}

let lastViewport: Size = { width: 0, height: 0 };

function readViewport(): Size {
  const root = document.documentElement;
  // jsdom lays nothing out, so the root has no client size; the window still has one.
  const width = root.clientWidth || window.innerWidth;
  const height = root.clientHeight || window.innerHeight;
  if (width !== lastViewport.width || height !== lastViewport.height) {
    lastViewport = { width, height };
  }
  return lastViewport;
}

/** The layout viewport without scrollbars, the box fixed elements are placed in. */
export function useViewport(): Size {
  return useSyncExternalStore(subscribeViewport, readViewport, readViewport);
}
